/**
 * "Select all" / "Deselect all", injected into uPlot's own legend box.
 *
 * uPlot gives one click per series; these are the "all" and "none" ends of
 * that, which is what makes isolating a single line out of a multi-ship overlay
 * quick. They live *inside* the legend rather than in a Lit-rendered strip below
 * it, so the bulk controls and the rows they act on read as one surface.
 *
 * That means raw DOM, not a template: the legend is built by uPlot and Lit owns
 * neither it nor anything inside it. The row is appended to the legend's tbody
 * and styled back to a full-width block (uPlot's inline legend lays every
 * descendant out as `inline-block`), which is what puts it bottom-left.
 *
 * Only a chart with more than one plotted series gets them: uPlot hides the
 * legend entirely below two (`buildLegendOpts`), and a panel showing a single
 * signal has nothing worth bulk-toggling.
 */

import type uPlot from 'uplot';
import { plotState } from '../../../state/plot-state.ts';
import { setAllSeriesVisible } from '../plot-runtime.ts';

function toolButton(label: string, title: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'chart-series-btn';
  btn.type = 'button';
  btn.textContent = label;
  btn.title = title;
  btn.addEventListener('click', onClick);
  return btn;
}

/**
 * Add the bulk controls to `u`'s legend.
 *
 * `scope` is the instance they drive: omitted on the single/overlay chart (where
 * there is only one), and the panel itself in grid, so a panel's buttons toggle
 * that panel rather than the whole grid.
 */
export function attachLegendTools(
  u: uPlot,
  scope?: uPlot,
  /** A tile passes the style it was saved with; Explore uses the live one. */
  enabled: boolean = plotState.value.style.legend_tools,
): void {
  if (!enabled) return;
  // series[0] is uPlot's x and is never a legend row.
  if (u.series.length < 3) return;
  const legend = u.root.querySelector<HTMLElement>('.u-legend');
  if (!legend) return;
  const body = legend.querySelector('tbody');
  if (!body || body.querySelector('.chart-series-tools')) return;

  const row = document.createElement('tr');
  row.className = 'chart-series-tools';
  const cell = document.createElement('th');
  cell.colSpan = 2;

  const label = document.createElement('span');
  label.className = 'chart-series-label';
  label.textContent = 'Series';
  cell.appendChild(label);
  cell.appendChild(
    toolButton('Select all', 'Show every series', () => setAllSeriesVisible(true, scope)),
  );
  cell.appendChild(
    toolButton(
      'Deselect all',
      'Hide every series - then click one in the legend to isolate it',
      () => setAllSeriesVisible(false, scope),
    ),
  );
  row.appendChild(cell);
  body.appendChild(row);
}
